// Hook is responsible to enable developer to acknowledge if modal of creation of a log request can be shown to the end user.

import {
  enumDeviceLogStatus,
  searchFilterFilterType,
  searchOperator,
} from '@ingenico/api-sdk-device-management-private-v2';
import { LNGS_NAMESPACE_LOGICAL_DEVICE } from '@ingenico/util-i18n-config';
import { useIntervalEffect } from '@react-hookz/web';
import { isEqual, toString } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceConnectionStatusType } from '../domain/models';
import { useGetDeviceIdV2 } from './useGetDeviceIdV2.hook';
import { useGetDeviceLogsByIdV2 } from './useGetDeviceLogsByIdV2.hook';
import { useDeviceManagementCapability } from './useDeviceManagementCapability.hook';

export interface CapabilityCreateLogcatReturn {
  readonly canDisplayKebabMenuDeviceLogs: boolean;
  readonly canDisplaySubItemMenuCreateLogs: boolean;
  readonly isEnabledSubItemMenuCreateLogs: boolean;
  readonly errorMessage: string;
  readonly refetchIsInProgress: () => void;
}
interface CapabilityCreateLogcatProps {
  readonly isAttachedDevice: boolean;
  readonly deviceId: string;
}

const DELAY_REFETCH = 10 * 1000; //10 Seconds
export function useCapabilityCreateLogcat({
  isAttachedDevice,
  deviceId,
}: CapabilityCreateLogcatProps): CapabilityCreateLogcatReturn {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_LOGICAL_DEVICE));
  const canReadLogs = useDeviceManagementCapability('logicalDevice.logcat.read');
  const canCreateLogs = useDeviceManagementCapability('logicalDevice.logcat.edit');

  const {
    isSuccess: isSuccessGetDeviceIdV2,
    data: deviceIdV2,
    refetch: refetchGetDeviceIdV2,
  } = useGetDeviceIdV2({ deviceId, shouldFetch: isAttachedDevice && canReadLogs });

  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (
      isSuccessGetDeviceIdV2 &&
      deviceIdV2?.isLogcatReady &&
      isEqual(deviceIdV2?.connection?.connectionStatus, DeviceConnectionStatusType.Connected)
    ) {
      setIsConnected(true);
    } else if (
      isSuccessGetDeviceIdV2 &&
      isEqual(deviceIdV2?.connection?.connectionStatus, DeviceConnectionStatusType.Disconnected)
    ) {
      setIsConnected(false);
    }
  }, [isSuccessGetDeviceIdV2, deviceIdV2?.connection?.connectionStatus, setIsConnected, deviceIdV2?.isLogcatReady]);

  const [hasConnectedAtLeastOnce, setHasConnectedAtLeastOnce] = useState(false);

  useEffect(() => {
    if (!hasConnectedAtLeastOnce && isConnected) {
      setHasConnectedAtLeastOnce(true);
    }
  }, [isConnected, hasConnectedAtLeastOnce]);

  const useGetDeviceLogsByIdV2Content = useMemo(() => {
    return {
      deviceId,
      shouldFetch: hasConnectedAtLeastOnce,
      body: {
        filters: [
          {
            name: 'deviceLog.status',
            filterType: searchFilterFilterType.array,
            operator: searchOperator.in,
            value: JSON.stringify([enumDeviceLogStatus.IN_PROGRESS]),
          },
        ],
        sort: {},
      },
      params: {
        offset: 0,
        limit: 1,
      },
    };
  }, [deviceId, hasConnectedAtLeastOnce]);

  const {
    isSuccess: isSuccessGetDeviceLogsIdV2,
    data: deviceLogsIdV2,
    refetch: refetchGetDeviceLogsByIdV2,
  } = useGetDeviceLogsByIdV2(useGetDeviceLogsByIdV2Content);

  const refetchBoth = useCallback<() => void>(() => {
    refetchGetDeviceIdV2();
  }, [refetchGetDeviceIdV2]);

  const hasNoInProgressLog = useMemo(() => {
    return isSuccessGetDeviceLogsIdV2 && deviceLogsIdV2?.data.length === 0;
  }, [deviceLogsIdV2?.data.length, isSuccessGetDeviceLogsIdV2]);

  const canDisplayKebabMenuDeviceLogs = useMemo(() => {
    return canReadLogs && deviceIdV2?.isLogcatReady === true && isAttachedDevice;
  }, [canReadLogs, deviceIdV2?.isLogcatReady, isAttachedDevice]);

  const canDisplaySubItemMenuCreateLogs = useMemo(() => {
    return canCreateLogs;
  }, [canCreateLogs]);

  const { isEnabledSubItemMenuCreateLogs, errorMessage } = useMemo(() => {
    if (!isConnected) {
      return {
        isEnabledSubItemMenuCreateLogs: false,
        errorMessage: toString(t('logcat.creation.tooltip.nonConnected')),
      };
    }

    if (!hasNoInProgressLog) {
      //Update to !hasNoInProgressLog
      return {
        isEnabledSubItemMenuCreateLogs: false,
        errorMessage: toString(t('logcat.creation.tooltip.logInProgress')),
      };
    }
    return {
      isEnabledSubItemMenuCreateLogs: true,
      errorMessage: '',
    };
  }, [isConnected, hasNoInProgressLog, t]);

  useIntervalEffect(refetchBoth, DELAY_REFETCH);

  return {
    canDisplayKebabMenuDeviceLogs,
    canDisplaySubItemMenuCreateLogs,
    isEnabledSubItemMenuCreateLogs,
    errorMessage,
    refetchIsInProgress: refetchGetDeviceLogsByIdV2,
  };
}
